import { distanceKilometres, walkingMinutes, type LocationPoint, type Venue } from '../planning.ts'
import {
  compareTravel,
  travelMetrics,
  withinTravelTolerance,
  type GroupRankedVenue,
  type GroupTravel,
  type RankingPreference,
} from './candidates.ts'
import type { MultimodalNode, MultimodalRoute, ScheduledMode } from '../multimodal/data.ts'
import type { TransitRoute, TransitStation } from './data.ts'
import type {
  ArriveByStationJourney,
  ExactTransfer,
  StationAccess,
  StationJourney,
  TransitResult,
} from './router.ts'
import type { ScheduledModeSet } from '../multimodal/protocol.ts'

export type RoadMode = 'taxi' | 'bike'
export type TravelMode = 'walk' | ScheduledMode | RoadMode | 'bike-share'
export type TravelPermissionSet = [TravelMode, ...TravelMode[]]
export type TravelChoice =
  | { kind: 'permissions'; modes: TravelPermissionSet }
  | { kind: 'none' }

type RouteEndpoint = TransitStation | MultimodalNode
type JourneyRoute = TransitRoute | MultimodalRoute

export interface ScheduledRoutedPerson {
  allowedModes: ScheduledModeSet
  feedVersion?: string
  bicycleAccess?: boolean
  bikeShareAccess?: boolean
  bikeShareEgressByVenue?: Array<BikeShareTransitEgress | null>
  bikeShareEgressOptionsByVenue?: BikeShareTransitEgress[][]
  access?: StationAccess[]
  exactTransfers?: ExactTransfer[]
  result: TransitResult
  endpoints: RouteEndpoint[]
  routes: JourneyRoute[]
  walkingEgressByVenue?: Array<Array<{ endpointIndex: number; seconds: number }>>
  arriveByJourneys?: Array<ArriveByStationJourney | null>
}

interface RoadRoutedPerson {
  mode: RoadMode
  durations: Array<number | null>
}

export interface BikeShareStationRef {
  id: string
  name: string
  lat: number
  lon: number
}

export interface BikeShareRoutedPerson {
  kind: 'bike-share'
  journeys: Array<BikeShareEndpointJourney | null>
  snapshotUpdatedAt: number
}

export type RoutedPerson = ScheduledRoutedPerson | RoadRoutedPerson | BikeShareRoutedPerson

interface WalkingRoutedAlternative {
  kind: 'walk'
  origin: LocationPoint
  durations?: Array<number | null>
}

export type RoutedAlternative = RoutedPerson | WalkingRoutedAlternative

interface AlternativeFailure {
  mode: TravelMode
  message: string
}

export interface RoutedParticipant {
  permissions: TravelPermissionSet
  alternatives: RoutedAlternative[]
  failures: AlternativeFailure[]
  arrivalSeconds?: number
}

interface WalkingEndpointJourney {
  kind: 'walk'
  endpointIndex: number
  durationSeconds: number
  source: 'estimate' | 'routed'
}

interface ScheduledEndpointJourney {
  kind: 'scheduled'
  endpointIndex: number
  journey: StationJourney
  egressSeconds: number
  durationSeconds: number
  departureSeconds?: number
  venueArrivalSeconds?: number
  commitmentSeconds?: number
  bikeShareEgress?: BikeShareTransitEgress
}

export interface BikeShareTransitEgress {
  stationIndex: number
  pickupName: string
  returnName: string
  pickupWalkSeconds: number
  rideSeconds: number
  returnWalkSeconds: number
  seconds: number
  walkingSource: 'routed'
}

interface RoadEndpointJourney {
  kind: 'road'
  mode: RoadMode
  endpointIndex: number
  durationSeconds: number
}

export interface BikeShareEndpointJourney {
  kind: 'bike-share'
  endpointIndex: number
  pickupStation: BikeShareStationRef
  returnStation: BikeShareStationRef
  accessSeconds: number
  rideSeconds: number
  egressSeconds: number
  durationSeconds: number
  walkingSource: 'routed'
}

interface JourneyTiming {
  departureSeconds?: number
  venueArrivalSeconds?: number
  commitmentSeconds?: number
}

export type EndpointJourney = (
  | WalkingEndpointJourney
  | ScheduledEndpointJourney
  | RoadEndpointJourney
  | BikeShareEndpointJourney) & JourneyTiming

export interface ModeCandidateArea extends GroupTravel {
  lat: number
  lon: number
  journeys: EndpointJourney[]
  withinMaximum: boolean
}

export interface ModeRankedVenue extends GroupRankedVenue {
  journeys: EndpointJourney[]
}

export type ModeRouteStep = {
  kind: 'ride'
  label: string
  mode: ScheduledMode
  origin: string
  destination: string
} | {
  kind: 'walk' | 'bike' | 'bike-share' | 'transfer'
  seconds: number
  destination: string
}

export interface ModeRouteDetails {
  endpoint: string
  steps: ModeRouteStep[]
}

export function formatJourneyMinutes(seconds: number): string {
  if (seconds === 0) return '0 min'
  return seconds < 60 ? '<1 min' : `${Math.round(seconds / 60)} min`
}

export function journeyWalkingTime(journey: EndpointJourney): { seconds: number; complete: boolean } | null {
  if (journey.kind === 'road') return null
  if (journey.kind === 'walk') return { seconds: journey.durationSeconds, complete: true }
  if (journey.kind === 'bike-share') {
    return { seconds: journey.accessSeconds + journey.egressSeconds, complete: true }
  }
  const legs = journey.journey.legs
  if (!legs.length || legs[0].kind !== 'access') return null
  let seconds = journey.bikeShareEgress
    ? journey.bikeShareEgress.pickupWalkSeconds + journey.bikeShareEgress.returnWalkSeconds
    : journey.egressSeconds
  let complete = true
  for (const [index, leg] of legs.entries()) {
    if (leg.kind === 'access') {
      if (leg.mode === 'bike-share') {
        if (!leg.bikeShare) return null
        seconds += leg.bikeShare.pickupWalkSeconds + leg.bikeShare.returnWalkSeconds
      } else if (leg.mode !== 'bike') seconds += leg.travelSeconds ?? leg.seconds
    } else if (leg.kind === 'transfer') {
      if (leg.walkingSeconds !== undefined) seconds += leg.walkingSeconds
      else if (leg.seconds > 0) complete = false
    } else if (legs[index - 1]?.kind === 'ride') {
      // Same-station changes have no transfer leg or measured walking duration.
      complete = false
    }
  }
  return { seconds, complete }
}

const ENDPOINT_GRID_LATITUDE = 0.01
const ENDPOINT_GRID_LONGITUDE = 0.012

export function candidateModeAreas(
  people: RoutedPerson[],
  preference: RankingPreference,
  maximumMinutes: number | null,
  maximumAreas = 3,
  separationKm = 0.8,
): ModeCandidateArea[] {
  const scheduledPeople = people.filter(isScheduledPerson)
  if (!scheduledPeople.length) return []
  const seeds = new Map<string, { lat: number; lon: number }>()
  for (const person of scheduledPeople) {
    person.result.journeys.forEach((journey, endpointIndex) => {
      const endpoint = person.endpoints[endpointIndex]
      if (!journey || !endpoint) return
      const key = `${endpoint[2]},${endpoint[3]}`
      if (!seeds.has(key)) seeds.set(key, { lat: endpoint[2], lon: endpoint[3] })
    })
  }
  const indexes = scheduledPeople.map((person) => endpointIndex(person))
  const candidates = [...seeds.values()].flatMap((seed) => {
    const journeys = scheduledPeople.map((person, index) => bestJourneyToPoint(person, indexes[index], seed))
    if (journeys.some((journey) => !journey)) return []
    const resolved = journeys as EndpointJourney[]
    const metrics = travelMetrics(resolved.map((journey) => journeyMinutes(journey)), preference)
    return [{
      ...seed,
      ...metrics,
      journeys: resolved,
      withinMaximum: maximumMinutes === null || metrics.maximum <= maximumMinutes,
    }]
  }).sort((left, right) => Number(right.withinMaximum) - Number(left.withinMaximum)
    || compareTravel(left, right, preference))

  const selected: ModeCandidateArea[] = []
  for (const candidate of candidates) {
    if (selected.every((area) => distanceKilometres(area, candidate) >= separationKm)) selected.push(candidate)
    if (selected.length === maximumAreas) break
  }
  return selected
}

export function rankModeVenues(
  venues: Venue[],
  people: RoutedPerson[],
  preference: RankingPreference,
  maximumMinutes: number | null,
): ModeRankedVenue[] {
  const indexes = people.map((person) => isScheduledPerson(person) ? endpointIndex(person) : null)
  const candidates = venues.flatMap((venue, venueIndex) => {
    const journeys = people.map((person, index) => isScheduledPerson(person)
      ? bestJourneyToPoint(person, indexes[index]!, venue, venueIndex)
      : isBikeSharePerson(person) ? person.journeys[venueIndex] : directJourney(person, venueIndex))
    if (journeys.some((journey) => !journey)) return []
    const resolved = journeys as EndpointJourney[]
    const metrics = travelMetrics(resolved.map((journey) => journeyMinutes(journey)), preference)
    if (maximumMinutes !== null && metrics.maximum > maximumMinutes) return []
    return [{ ...venue, ...metrics, journeys: resolved }]
  })
  return rankVenueTiers(candidates, preference)
}

export function rankParticipantVenues(
  venues: Venue[],
  participants: RoutedParticipant[],
  preference: RankingPreference,
  maximumMinutes: number | null,
  requireRoutedWalking = false,
): ModeRankedVenue[] {
  const candidates = venues.flatMap((venue, venueIndex) => {
    const journeys = participants.map((participant) =>
      bestJourneyForVenue(participant, venue, venueIndex, requireRoutedWalking))
    if (journeys.some((journey) => !journey)) return []
    const resolved = journeys as EndpointJourney[]
    const metrics = travelMetrics(resolved.map((journey) => journeyMinutes(journey)), preference)
    if (maximumMinutes !== null && metrics.maximum > maximumMinutes) return []
    return [{ ...venue, ...metrics, journeys: resolved }]
  })
  return rankVenueTiers(candidates, preference)
}

function rankVenueTiers(venues: ModeRankedVenue[], preference: RankingPreference): ModeRankedVenue[] {
  let remaining = [...venues].sort((left, right) => compareTravel(left, right, preference))
  const ranked: ModeRankedVenue[] = []
  while (remaining.length) {
    const best = remaining[0]
    const tier = remaining.filter((venue) => withinTravelTolerance(best, venue, preference))
    const tierIds = new Set(tier.map(({ id }) => id))
    tier.sort((left, right) => wheelchairPriority(right) - wheelchairPriority(left)
      || compareTravel(left, right, preference))
    ranked.push(...tier)
    remaining = remaining.filter(({ id }) => !tierIds.has(id))
  }
  return ranked
}

function wheelchairPriority(venue: Venue): number {
  if (venue.wheelchair === 'yes') return 2
  if (venue.wheelchair === 'limited') return 1
  return 0
}

export function scheduledAlternatives(participant: RoutedParticipant): ScheduledRoutedPerson[] {
  return participant.alternatives.filter((alternative): alternative is ScheduledRoutedPerson =>
    !('origin' in alternative) && isScheduledPerson(alternative))
}

export function selectedAlternative(
  participant: RoutedParticipant,
  journey: EndpointJourney,
): RoutedAlternative {
  const alternative = participant.alternatives.find((candidate) => {
    if (journey.kind === 'walk') return 'origin' in candidate
    if (journey.kind === 'scheduled') return !('origin' in candidate) && isScheduledPerson(candidate)
    if (journey.kind === 'bike-share') return !('origin' in candidate) && isBikeSharePerson(candidate)
    return !('origin' in candidate) && !isScheduledPerson(candidate)
      && !isBikeSharePerson(candidate) && candidate.mode === journey.mode
  })
  if (!alternative) throw new Error('Selected journey has no routed alternative.')
  return alternative
}

export function bestJourneyForVenue(
  participant: RoutedParticipant,
  venue: Venue,
  venueIndex: number,
  requireRoutedWalking = false,
): EndpointJourney | null {
  let best: EndpointJourney | null = null
  for (const alternative of participant.alternatives) {
    if (!alternativeAllowed(participant.permissions, alternative)) continue
    let candidate: EndpointJourney | null
    if ('origin' in alternative) {
      const routedSeconds = alternative.durations?.[venueIndex]
      candidate = routedSeconds !== null && routedSeconds !== undefined
        ? { kind: 'walk', endpointIndex: venueIndex, durationSeconds: routedSeconds, source: 'routed' }
        : requireRoutedWalking ? null : {
          kind: 'walk', endpointIndex: venueIndex,
          durationSeconds: walkingMinutes(alternative.origin, venue) * 60, source: 'estimate',
        }
    } else if (isScheduledPerson(alternative)) {
      candidate = participant.arrivalSeconds !== undefined && alternative.arriveByJourneys === undefined
        ? null
        : bestJourneyToPoint(
            alternative, endpointIndex(alternative), venue, venueIndex, requireRoutedWalking,
          )
    } else if (isBikeSharePerson(alternative)) {
      const bikeShare = alternative.journeys[venueIndex]
      candidate = requireRoutedWalking && bikeShare?.walkingSource !== 'routed' ? null : bikeShare
    } else {
      candidate = directJourney(alternative, venueIndex)
    }
    if (candidate && participant.arrivalSeconds !== undefined && candidate.commitmentSeconds === undefined) {
      candidate = {
        ...candidate,
        departureSeconds: participant.arrivalSeconds - candidate.durationSeconds,
        venueArrivalSeconds: participant.arrivalSeconds,
        commitmentSeconds: candidate.durationSeconds,
      }
    }
    if (candidate && (!best || journeySeconds(candidate) < journeySeconds(best))) best = candidate
  }
  return best
}

function alternativeAllowed(permissions: TravelPermissionSet, alternative: RoutedAlternative): boolean {
  if ('origin' in alternative) return permissions.includes('walk')
  if (isScheduledPerson(alternative)) {
    const scheduledPermissions = permissions.filter((mode): mode is ScheduledMode =>
      mode === 'subway' || mode === 'bus' || mode === 'ferry')
    return alternative.allowedModes.length === scheduledPermissions.length
      && alternative.allowedModes.every((mode, index) => mode === scheduledPermissions[index])
      && (!alternative.bicycleAccess || permissions.includes('bike'))
      && (!alternative.bikeShareAccess || permissions.includes('bike-share'))
  }
  if (isBikeSharePerson(alternative)) return permissions.includes('bike-share')
  return permissions.includes(alternative.mode)
}

export function modeRouteSummary(person: RoutedAlternative, routed: EndpointJourney): string {
  if ('origin' in person) {
    if (routed.kind !== 'walk') throw new Error('Walking person and journey types do not match.')
    return routed.source === 'routed' ? 'Walk' : 'Walk estimate'
  }
  const bikeShareJourney = isBikeShareJourney(routed)
  if (isBikeSharePerson(person)) {
    if (!bikeShareJourney) throw new Error('Bike-share person and journey types do not match.')
    const access = formatJourneyMinutes(routed.accessSeconds)
    const ride = formatJourneyMinutes(routed.rideSeconds)
    const egress = formatJourneyMinutes(routed.egressSeconds)
    return `walk ${access} to ${routed.pickupStation.name} → bike ${ride} to ${routed.returnStation.name} → walk ${egress}`
  }
  if (bikeShareJourney) throw new Error('Bike-share person and journey types do not match.')
  if (!isScheduledPerson(person)) {
    if (!isRoadJourney(routed)) throw new Error('Road person has another journey type.')
    return `${modeLabel(person.mode)} estimate`
  }
  if (!isScheduledJourney(routed)) throw new Error('Scheduled person has another journey type.')
  const details = modeRouteDetails(person, routed)
  const steps = details.steps.map((step) => step.kind === 'ride'
    ? `${step.label} ${serviceLabel(step.mode)}${step.origin ? ` from ${step.origin}` : ''}${step.destination ? ` to ${step.destination}` : ''}`
    : step.kind === 'bike'
      ? `bike ${formatJourneyMinutes(step.seconds)} and park${step.destination ? ` at ${step.destination}` : ''}`
      : step.kind === 'bike-share'
        ? `bike share ${formatJourneyMinutes(step.seconds)}${step.destination ? ` to ${step.destination}` : ''}`
      : `${step.kind} ${formatJourneyMinutes(step.seconds)}${step.destination ? ` to ${step.destination}` : ''}`)
  return steps.length
    ? `${steps.join(' → ')}${routed.journey.legs.length ? '' : ` to ${details.endpoint}`}`
    : `Walk to ${details.endpoint}`
}

export function modeRouteDetails(person: RoutedPerson, routed: EndpointJourney): ModeRouteDetails {
  if (!isScheduledPerson(person) || !('journey' in routed)) return { endpoint: '', steps: [] }
  const steps: ModeRouteStep[] = []
  if (!routed.journey.legs.length && routed.journey.routeIndexes?.length) {
    for (const routeIndex of routed.journey.routeIndexes) {
      const route = person.routes[routeIndex]
      const label = route?.[1]
      const mode = route && label
        ? (route.length > 3 ? route[3] : singletonMode(person.allowedModes))
        : null
      const previous = steps.at(-1)
      if (label && mode && (previous?.kind !== 'ride' || previous.label !== label)) {
        steps.push({ kind: 'ride', label, mode, origin: '', destination: '' })
      }
    }
  }
  for (const leg of routed.journey.legs) {
    if (leg.kind === 'access' && leg.mode === 'bike-share' && leg.bikeShare) {
      const destination = person.endpoints[leg.toStation]?.[1] ?? ''
      steps.push(
        {
          kind: 'walk',
          seconds: leg.bikeShare.pickupWalkSeconds,
          destination: leg.bikeShare.pickupName,
        },
        {
          kind: 'bike-share',
          seconds: leg.bikeShare.rideSeconds,
          destination: leg.bikeShare.returnName,
        },
        {
          kind: 'walk',
          seconds: leg.bikeShare.returnWalkSeconds,
          destination,
        },
      )
    } else if (leg.kind === 'access') {
      const destination = person.endpoints[leg.toStation]?.[1] ?? ''
      steps.push({
        kind: leg.mode === 'bike' ? 'bike' : leg.mode === 'bike-share' ? 'bike-share' : 'walk',
        seconds: leg.travelSeconds ?? leg.seconds,
        destination,
      })
    } else if (leg.kind === 'ride') {
      const route = person.routes[leg.routeIndex]
      const label = route?.[1]
      if (route && label) {
        const endpointMode = person.endpoints[leg.fromStation]?.[4]
        const mode = (route.length > 3 ? route[3] : endpointMode) ?? singletonMode(person.allowedModes)
        if (!mode) throw new Error('A multimodal route is missing its scheduled mode.')
        steps.push({
          kind: 'ride', label, mode,
          origin: person.endpoints[leg.fromStation]?.[1] ?? '',
          destination: person.endpoints[leg.toStation]?.[1] ?? '',
        })
      }
    } else if (leg.kind === 'transfer') {
      const destination = person.endpoints[leg.toStation]?.[1]
      if (leg.walkingSeconds !== undefined || leg.seconds > 0 || leg.fromStation !== leg.toStation) {
        steps.push({
          kind: leg.walkingSeconds === undefined ? 'transfer' : 'walk',
          seconds: leg.walkingSeconds ?? leg.seconds,
          destination: destination ?? '',
        })
      }
    }
  }
  if (routed.journey.legs.length && routed.bikeShareEgress) {
    steps.push(
      {
        kind: 'walk',
        seconds: routed.bikeShareEgress.pickupWalkSeconds,
        destination: routed.bikeShareEgress.pickupName,
      },
      {
        kind: 'bike-share',
        seconds: routed.bikeShareEgress.rideSeconds,
        destination: routed.bikeShareEgress.returnName,
      },
      {
        kind: 'walk',
        seconds: routed.bikeShareEgress.returnWalkSeconds,
        destination: 'destination',
      },
    )
  } else if (routed.journey.legs.length) {
    steps.push({ kind: 'walk', seconds: routed.egressSeconds, destination: 'destination' })
  }
  const endpoint = routed.journey.legs.length
    ? 'destination'
    : person.endpoints[routed.endpointIndex]?.[1] ?? 'transit stop'
  return { endpoint, steps }
}

function singletonMode(modes: ScheduledModeSet): ScheduledMode | null {
  return modes.length === 1 ? modes[0] : null
}

function serviceLabel(mode: ScheduledMode): string {
  return mode === 'subway' ? 'train' : mode
}

function modeLabel(mode: TravelMode): string {
  if (mode === 'taxi') return 'Taxi / ride-share'
  if (mode === 'bike') return 'Bicycle'
  if (mode === 'bike-share') return 'Bike share'
  return `${mode.charAt(0).toUpperCase()}${mode.slice(1)}`
}

function bestJourneyToPoint(
  person: ScheduledRoutedPerson,
  index: Map<string, number[]>,
  point: { lat: number; lon: number },
  venueIndex?: number,
  requireRoutedWalking = false,
): ScheduledEndpointJourney | null {
  if (venueIndex !== undefined && person.arriveByJourneys !== undefined) {
    const journey = person.arriveByJourneys[venueIndex]
    if (!journey || journey.boardings < 1) return null
    const walkingEgress = person.walkingEgressByVenue?.[venueIndex]?.some((egress) =>
      egress.endpointIndex === journey.egressStationIndex && egress.seconds === journey.egressSeconds)
    const bikeShareEgress = walkingEgress ? undefined : [
      ...(person.bikeShareEgressOptionsByVenue?.[venueIndex] ?? []),
      ...(person.bikeShareEgressByVenue?.[venueIndex] ? [person.bikeShareEgressByVenue[venueIndex]!] : []),
    ].find((egress) => egress.stationIndex === journey.egressStationIndex
      && egress.seconds === journey.egressSeconds)
    return {
      kind: 'scheduled',
      endpointIndex: journey.egressStationIndex,
      journey,
      egressSeconds: journey.egressSeconds,
      durationSeconds: journey.durationSeconds + journey.egressSeconds,
      departureSeconds: journey.departureSeconds,
      venueArrivalSeconds: journey.venueArrivalSeconds,
      commitmentSeconds: journey.commitmentSeconds,
      ...(bikeShareEgress ? { bikeShareEgress } : {}),
    }
  }
  const maximumEgressKm = person.allowedModes.length === 1 && person.allowedModes[0] === 'bus' ? 0.8 : 1.2
  const latitude = Math.floor(point.lat / ENDPOINT_GRID_LATITUDE)
  const longitude = Math.floor(point.lon / ENDPOINT_GRID_LONGITUDE)
  const latitudeRange = Math.ceil(maximumEgressKm / (111 * ENDPOINT_GRID_LATITUDE))
  const longitudeRange = Math.ceil(maximumEgressKm / (84 * ENDPOINT_GRID_LONGITUDE))
  let best: ScheduledEndpointJourney | null = null
  const routedEgress = venueIndex === undefined ? undefined : person.walkingEgressByVenue?.[venueIndex]
  if (routedEgress) {
    for (const { endpointIndex, seconds: egressSeconds } of routedEgress) {
      const journey = person.result.journeys[endpointIndex]
      if (!journey || journey.boardings < 1) continue
      const durationSeconds = journey.durationSeconds + egressSeconds
      if (!best || durationSeconds < best.durationSeconds) {
        best = { kind: 'scheduled', endpointIndex, journey, egressSeconds, durationSeconds }
      }
    }
  } else if (!requireRoutedWalking) {
    for (let latOffset = -latitudeRange; latOffset <= latitudeRange; latOffset += 1) {
      for (let lonOffset = -longitudeRange; lonOffset <= longitudeRange; lonOffset += 1) {
        const nearby = index.get(`${latitude + latOffset}:${longitude + lonOffset}`) ?? []
        for (const endpointIndex of nearby) {
          const journey = person.result.journeys[endpointIndex]
          const endpoint = person.endpoints[endpointIndex]
          if (!journey || journey.boardings < 1 || !endpoint) continue
          const endpointMaximumEgressKm = endpoint[4] === 'bus'
            || (!endpoint[4] && person.allowedModes.length === 1 && person.allowedModes[0] === 'bus') ? 0.8 : 1.2
          const distance = distanceKilometres({ lat: endpoint[2], lon: endpoint[3] }, point)
          if (distance > endpointMaximumEgressKm) continue
          const egressSeconds = Math.max(60, Math.round((distance * 1.2 / 4.8) * 3600))
          const durationSeconds = journey.durationSeconds + egressSeconds
          if (!best || durationSeconds < best.durationSeconds) {
            best = { kind: 'scheduled', endpointIndex, journey, egressSeconds, durationSeconds }
          }
        }
      }
    }
  }
  const exactBikeShare = venueIndex === undefined ? undefined : person.bikeShareEgressByVenue?.[venueIndex]
  if (exactBikeShare) {
    const journey = person.result.journeys[exactBikeShare.stationIndex]
    if (journey && journey.boardings >= 1) {
      const durationSeconds = journey.durationSeconds + exactBikeShare.seconds
      if (!best || durationSeconds < best.durationSeconds) {
        best = {
          kind: 'scheduled',
          endpointIndex: exactBikeShare.stationIndex,
          journey,
          egressSeconds: exactBikeShare.seconds,
          durationSeconds,
          bikeShareEgress: exactBikeShare,
        }
      }
    }
  } else if (venueIndex === undefined && person.bikeShareAccess) {
    person.endpoints.forEach((endpoint, endpointIndex) => {
      const journey = person.result.journeys[endpointIndex]
      if (!journey || journey.boardings < 1) return
      const distance = distanceKilometres({ lat: endpoint[2], lon: endpoint[3] }, point)
      if (distance > 8) return
      const egressSeconds = Math.max(60, Math.round((distance * 1.15 / 15) * 3600)) + 6 * 60
      const durationSeconds = journey.durationSeconds + egressSeconds
      if (!best || durationSeconds < best.durationSeconds) {
        best = { kind: 'scheduled', endpointIndex, journey, egressSeconds, durationSeconds }
      }
    })
  }
  return best
}

export function walkingEgressCandidateIndexes(
  person: ScheduledRoutedPerson,
  venue: Venue,
  maximum = 2,
): number[] {
  return person.endpoints.flatMap((endpoint, endpointIndex) => {
    if (endpoint[4] && !person.allowedModes.includes(endpoint[4])) return []
    const journey = person.result.journeys[endpointIndex]
    if (!journey || journey.boardings < 1) return []
    const maximumEgressKm = endpoint[4] === 'bus'
      || (!endpoint[4] && person.allowedModes.length === 1 && person.allowedModes[0] === 'bus') ? 0.8 : 1.2
    const distance = distanceKilometres({ lat: endpoint[2], lon: endpoint[3] }, venue)
    return distance <= maximumEgressKm
      ? [{ endpointIndex, estimatedSeconds: journey.durationSeconds
        + walkingMinutes({ lat: endpoint[2], lon: endpoint[3] }, venue) * 60 }]
      : []
  }).sort((left, right) => left.estimatedSeconds - right.estimatedSeconds
    || left.endpointIndex - right.endpointIndex)
    .slice(0, maximum)
    .map(({ endpointIndex }) => endpointIndex)
}

export function geometricWalkingEgressCandidateIndexes(
  person: ScheduledRoutedPerson,
  venue: Venue,
  maximum = 2,
): number[] {
  return person.endpoints.flatMap((endpoint, endpointIndex) => {
    if (endpoint[4] && !person.allowedModes.includes(endpoint[4])) return []
    const maximumEgressKm = endpoint[4] === 'bus'
      || (!endpoint[4] && person.allowedModes.length === 1 && person.allowedModes[0] === 'bus') ? 0.8 : 1.2
    const distance = distanceKilometres({ lat: endpoint[2], lon: endpoint[3] }, venue)
    return distance <= maximumEgressKm ? [{ endpointIndex, distance }] : []
  }).sort((left, right) => left.distance - right.distance || left.endpointIndex - right.endpointIndex)
    .slice(0, maximum)
    .map(({ endpointIndex }) => endpointIndex)
}

function journeySeconds(journey: EndpointJourney): number {
  return journey.commitmentSeconds ?? journey.durationSeconds
}

function journeyMinutes(journey: EndpointJourney): number {
  return Math.ceil(journeySeconds(journey) / 60)
}

function endpointIndex(person: ScheduledRoutedPerson): Map<string, number[]> {
  const index = new Map<string, number[]>()
  person.endpoints.forEach((endpoint, endpointIndex) => {
    if (!person.result.journeys[endpointIndex]) return
    const key = gridKey(endpoint[2], endpoint[3], ENDPOINT_GRID_LATITUDE, ENDPOINT_GRID_LONGITUDE)
    const values = index.get(key) ?? []
    values.push(endpointIndex)
    index.set(key, values)
  })
  return index
}

function gridKey(lat: number, lon: number, latitudeSize: number, longitudeSize: number): string {
  return `${Math.floor(lat / latitudeSize)}:${Math.floor(lon / longitudeSize)}`
}

function directJourney(person: RoadRoutedPerson, venueIndex: number): RoadEndpointJourney | null {
  const durationSeconds = person.durations[venueIndex]
  return durationSeconds === null || durationSeconds === undefined
    ? null
    : { kind: 'road', mode: person.mode, endpointIndex: venueIndex, durationSeconds }
}

export function isScheduledPerson(person: RoutedPerson): person is ScheduledRoutedPerson {
  return 'result' in person
}

export function isBikeSharePerson(person: RoutedPerson): person is BikeShareRoutedPerson {
  return 'kind' in person && person.kind === 'bike-share'
}

function isBikeShareJourney(journey: EndpointJourney): journey is BikeShareEndpointJourney {
  return 'kind' in journey && journey.kind === 'bike-share'
}

function isRoadJourney(journey: EndpointJourney): journey is RoadEndpointJourney {
  return journey.kind === 'road'
}

function isScheduledJourney(journey: EndpointJourney): journey is ScheduledEndpointJourney {
  return journey.kind === 'scheduled'
}
